import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import ApplicationLayout from 'v3/layouts/Application';
import { Button, Card, Text } from 'v3/components';
import Space from 'v3/components/Space';
import InfiniteScroll from 'v3/components/InfiniteScroll';
import theme from 'v3/theme';
import { useModal } from 'hooks';
import { cooperplaceApi } from 'services/api';
import Filter from './List/Filter';
import TrainingCard from './List/Card';
import RequestTraining from './RequestTraining';
import EditLinkConfigModal from './Modals/EditLinkConfig';
import CloseLinkChecklistModal from './Modals/CloseLink';
import TrainingAnsweredResponses from './Modals/SeeResponses';

function formatDateBR(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const RESPONSE_VALIDITY_DAYS = 365;

const PAGE_STATUS = {
  loading: 'loading',
  ready: 'ready',
  accessDenied: 'accessDenied',
  error: 'error',
};

function FeedbackState({ title, message, onRetry }) {
  return (
    <Card bodyCardPadding="3rem 1.5rem">
      <div
        role="status"
        style={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          justifyContent: 'center',
          minHeight: 220,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            alignItems: 'center',
            backgroundColor: theme.colors.light_yellow,
            borderRadius: '50%',
            display: 'flex',
            height: 64,
            justifyContent: 'center',
            width: 64,
          }}
        >
          <AlertTriangle aria-hidden="true" color={theme.colors.warning} size={32} />
        </div>

        <Text as="h2" color="dark" type="regular" weight={600} style={{ margin: 0 }}>
          {title}
        </Text>
        <Text as="p" color="gray" type="regular" style={{ margin: 0, maxWidth: 680 }}>
          {message}
        </Text>

        {onRetry && (
          <Button onClick={onRetry} style={{ marginTop: 8 }}>
            <Text color="white" type="regular" weight={600}>
              Tentar novamente
            </Text>
          </Button>
        )}
      </div>
    </Card>
  );
}

function isTrainingResponseActive(responseItem) {
  const answeredAt = toDate(responseItem?.answeredAt);
  if (!answeredAt) return !!responseItem?.isActive;

  const expiresAt = new Date(answeredAt);
  expiresAt.setDate(expiresAt.getDate() + RESPONSE_VALIDITY_DAYS);
  return expiresAt.getTime() >= Date.now();
}

function matchesResponseStatus(item, responseStatus) {
  if (!responseStatus || responseStatus === 'all') return true;

  const groups = Array.isArray(item?.questionsResponses)
    ? item.questionsResponses.filter((group) => !!group?.answeredAt)
    : [];

  if (!groups.length) return false;

  if (responseStatus === 'active') {
    return groups.some(isTrainingResponseActive);
  }

  if (responseStatus === 'expired') {
    return groups.some((group) => !isTrainingResponseActive(group));
  }

  return true;
}

function getLinkUrl(item) {
  return item?.link?.url || '';
}

function getExpireInfo(item) {
  const expirationDays = Number(item?.link?.expirationDays) || 0;
  const createdAt = toDate(item?.link?.createdAt);

  if (!expirationDays || !createdAt) {
    return {
      label: item?.isActive ? 'Sem expiração' : 'Encerrado',
      status: item?.isActive ? 'active' : 'inactive',
    };
  }

  const expiresAt = new Date(createdAt);
  expiresAt.setDate(expiresAt.getDate() + expirationDays);

  const now = new Date();
  const expired = expiresAt.getTime() < now.getTime();

  return {
    label: expired ? 'Expirado' : formatDateBR(expiresAt),
    status: expired ? 'expired' : item?.isActive ? 'active' : 'inactive',
  };
}

function countResponses(item) {
  const groups = Array.isArray(item?.questionsResponses) ? item.questionsResponses : [];
  return groups.filter((group) => !!group?.answeredAt).length;
}

function buildParams(filter, page) {
  const plates = Array.isArray(filter?.plates)
    ? filter.plates.map((item) => (typeof item === 'object' && item !== null ? item.id : item))
    : [];

  return {
    page,
    perPage: 10,
    name: filter?.q || undefined,
    plates: plates.length ? plates : undefined,
    searchResponse: filter?.searchResponse || undefined,
    isActive: typeof filter?.isActive === 'boolean' ? filter.isActive : undefined,
  };
}

function mapTrainingItem(item) {
  const expireInfo = getExpireInfo(item);
  const title = item?.name || 'Treinamento';
  const isActive = !!item?.isActive;
  const responsesCount = countResponses(item);

  return {
    id: item?.id,
    title,
    createdBy: item?.createdUser?.username || '—',
    description: item?.description || '',
    linkUrl: getLinkUrl(item),
    createdAt: item?.link?.createdAt || null,
    expirationDays: Number(item?.link?.expirationDays) || 0,
    responsesCount,
    expireLabel: expireInfo.label,
    expireStatus: expireInfo.status,
    isActive,
  };
}

export default function TrainingChecklistList() {
  const advancedFilters = useModal();
  const requestIdRef = useRef(0);
  const [pageStatus, setPageStatus] = useState(PAGE_STATUS.loading);
  const [filters, setFilters] = useState({
    q: '',
    plates: [],
    searchResponse: '',
    responseStatus: undefined,
    isActive: undefined,
  });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    lastPage: 1,
    total: 0,
  });
  const [advancedFiltersQtd, setAdvancedFiltersQtd] = useState(0);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [closingItem, setClosingItem] = useState(null);
  const [closingLoading, setClosingLoading] = useState(false);
  const [answersItem, setAnswersItem] = useState(null);
  const [viewAnswersLoadingId, setViewAnswersLoadingId] = useState(null);

  const fetchData = useCallback(async (activeFilters, page = 1) => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (page === 1) {
      setLoading(true);
      setIsLoadingMore(false);
      setPageStatus((currentStatus) =>
        currentStatus === PAGE_STATUS.ready ? currentStatus : PAGE_STATUS.loading,
      );
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await cooperplaceApi.get('/training-checklist', {
        params: buildParams(activeFilters, page),
      });

      if (requestId !== requestIdRef.current) return;

      const payload = response?.data || {};
      const rawRows = Array.isArray(payload?.data) ? payload.data : [];
      const filteredRawRows = rawRows.filter((item) =>
        matchesResponseStatus(item, activeFilters?.responseStatus),
      );
      const rows = filteredRawRows.map(mapTrainingItem);
      const meta = payload?.meta?.pagination || payload?.meta || {};

      setPagination({
        page: Number(meta?.current_page) || page,
        lastPage: Number(meta?.last_page) || 1,
        total: Number(meta?.total) || rows.length,
      });

      setList((prev) => (page === 1 ? rows : [...prev, ...rows]));
      setPageStatus(PAGE_STATUS.ready);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;

      setList([]);
      setPagination({ page: 1, lastPage: 1, total: 0 });
      setPageStatus(e?.response?.status === 403 ? PAGE_STATUS.accessDenied : PAGE_STATUS.error);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchData(filters, 1);
  }, [fetchData, filters]);

  async function refreshList() {
    await fetchData(filters, 1);
  }

  async function handleCopyLink(link) {
    try {
      await navigator.clipboard.writeText(link || '');
      return true;
    } catch (e) {
      return false;
    }
  }

  async function handleViewAnswers(item) {
    if (!item?.id) return;

    try {
      setViewAnswersLoadingId(item.id);
      const response = await cooperplaceApi.get(`/training-checklist/${item.id}`);
      const updated = mapTrainingItem(response?.data?.data || response?.data || {});
      setList((prev) => prev.map((current) => (current.id === updated.id ? updated : current)));
      setAnswersItem(updated);
    } catch (e) {
      setAnswersItem(item);
    } finally {
      setViewAnswersLoadingId(null);
    }
  }

  const isReady = pageStatus === PAGE_STATUS.ready;
  const shouldRenderList = pageStatus === PAGE_STATUS.loading || pageStatus === PAGE_STATUS.ready;

  return (
    <ApplicationLayout
      title="Treinamentos"
      RightComponent={
        isReady ? (
          <Space x="0.5rem">
            <Button variant="secondary" onClick={() => advancedFilters.open()}>
              <Text weight={500} color="dark" type="regular">
                {advancedFiltersQtd ? `Filtro avançado (${advancedFiltersQtd})` : 'Filtro avançado'}
              </Text>
            </Button>

            <Button
              onClick={() => setIsRequestModalOpen(true)}
              disabled={loading}
              title="Solicitar treinamento"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 700, lineHeight: '18px' }}>+</span>
                <Text type="regular" weight={600}>
                  Solicitar Treinamento
                </Text>
              </div>
            </Button>
          </Space>
        ) : null
      }
    >
      {isReady && (
        <Filter
          loading={loading}
          showAdvancedFilter={advancedFilters.isOpen}
          onCloseAdvanceFilterModal={() => advancedFilters.close()}
          setParentFilter={setFilters}
          setAdvancedFiltersQtd={setAdvancedFiltersQtd}
          onFilterChange={(next) => setFilters(next)}
        />
      )}

      {pageStatus === PAGE_STATUS.accessDenied && (
        <FeedbackState
          title="Acesso aos treinamentos indisponível"
          message="Seu usuário não pertence ao grupo de Treinamento. Solicite ao suporte via central de atendimentos CSD que adicione seu usuário a esse grupo para visualizar os checklists de treinamento."
        />
      )}

      {pageStatus === PAGE_STATUS.error && (
        <FeedbackState
          title="Não foi possível carregar os treinamentos."
          message="Ocorreu um erro ao buscar os checklists de treinamento. Tente novamente."
          onRetry={() => fetchData(filters, 1)}
        />
      )}

      {shouldRenderList && (
        <Card>
          <InfiniteScroll
            loading={
              pageStatus === PAGE_STATUS.loading ||
              (loading && !isLoadingMore)
            }
            data={list}
            hasMore={pagination.page < pagination.lastPage}
            scrollThreshold={0.7}
            onEndReach={() => {
              if (loading || isLoadingMore) return;
              if (pagination.page >= pagination.lastPage) return;
              fetchData(filters, pagination.page + 1);
            }}
            loader={
              isLoadingMore ? (
                <div
                  style={{
                    width: '100%',
                    padding: '12px 0',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Text type="body" color="dark">
                    Carregando...
                  </Text>
                </div>
              ) : null
            }
            renderItem={(item) => (
              <div key={item.id} style={{ width: '100%', flex: '0 0 100%', maxWidth: '100%' }}>
                <TrainingCard
                  item={item}
                  onCopyLink={handleCopyLink}
                  onEditConfig={() => setEditingItem(item)}
                  onCloseChecklist={() => setClosingItem(item)}
                  onViewAnswers={() => handleViewAnswers(item)}
                  viewAnswersLoading={viewAnswersLoadingId === item.id}
                />
              </div>
            )}
          />
        </Card>
      )}

      {isRequestModalOpen && (
        <RequestTraining
          open={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          onCreated={() => {
            setIsRequestModalOpen(false);
            refreshList();
          }}
        />
      )}

      <EditLinkConfigModal
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        onSave={async ({ days, closed }) => {
          if (!editingItem?.id) return;

          await cooperplaceApi.put(`/training-checklist/${editingItem.id}`, {
            expirationDays: Number(days) || 0,
            isClosed: !!closed,
          });

          await refreshList();
          setEditingItem(null);
        }}
        checklistTitle={editingItem?.title || ''}
        responsesCount={editingItem?.responsesCount || 0}
        initialDays={editingItem?.expirationDays || 0}
        initialCreatedAt={editingItem?.createdAt || null}
        initialClosed={!editingItem?.isActive}
        isLinkClosed={!editingItem?.isActive}
      />

      <CloseLinkChecklistModal
        open={!!closingItem}
        loading={closingLoading}
        loadingLabel="Encerrando..."
        isLinkClosed={!closingItem?.isActive}
        onClose={() => {
          if (closingLoading) return;
          setClosingItem(null);
        }}
        checklistTitle={closingItem?.title || ''}
        responsesCount={closingItem?.responsesCount || 0}
        onConfirm={async () => {
          if (!closingItem?.id || closingLoading || !closingItem?.isActive) return;

          try {
            setClosingLoading(true);
            await cooperplaceApi.put(`/training-checklist/${closingItem.id}`, {
              isClosed: true,
            });
            await refreshList();
            setClosingItem(null);
          } finally {
            setClosingLoading(false);
          }
        }}
      />

      <TrainingAnsweredResponses
        open={!!answersItem}
        onClose={() => setAnswersItem(null)}
        singleChecklistId={answersItem?.id}
        checklistTitle={answersItem?.title || ''}
      />
    </ApplicationLayout>
  );
}
